import { type ReactNode, useState } from 'react';

import {
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  type InputProps,
  InputRightElement,
} from '@chakra-ui/react';
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';
import { IconEyeOffOutline, IconEyeOutline } from '@/components/Icons';

import { useFormFieldContext } from '../FormField.ctx';
import { type FieldCommonProps } from '../FormField.types';

export type FieldPasswordProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'password';
  label?: ReactNode;
  helper?: ReactNode;
  startElement?: ReactNode;
} & Pick<InputProps, 'placeholder' | 'size' | 'autoFocus'> &
  FieldCommonProps<TFieldValues, TName>;

export const FieldPassword = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FieldPasswordProps<TFieldValues, TName>
) => {
  const { isDisabled } = useFormFieldContext();
  const [showPassword, setShowPassword] = useState(false);
  return (
    <Controller
      {...props}
      render={({ field }) => (
        <FormFieldItem>
          {!!props.label && <FormFieldLabel>{props.label}</FormFieldLabel>}
          <InputGroup size={props.size}>
            <Input
              borderRadius="xl"
              type={showPassword ? 'text' : 'password'}
              placeholder={props.placeholder}
              autoFocus={props.autoFocus}
              {...field}
            />
            {!!props.startElement && (
              <InputLeftElement>{props.startElement}</InputLeftElement>
            )}
            <InputRightElement>
              <IconButton
                isDisabled={isDisabled}
                onClick={() => setShowPassword((x) => !x)}
                aria-label={showPassword ? 'Hide password' : 'Show password'} // TODO: translation
                display="flex"
                size="sm"
                fontSize="xl"
                icon={showPassword ? <IconEyeOutline /> : <IconEyeOffOutline />}
                variant="unstyled"
              />
            </InputRightElement>
          </InputGroup>
          {!!props.helper && <FormFieldHelper>{props.helper}</FormFieldHelper>}
          <FormFieldError />
        </FormFieldItem>
      )}
    />
  );
};
